'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ratingModel } from '@/models/ratingModel';
import { MatchListGrid } from '@/components/match';
import type { MatchWithRating } from '@/components/match';
import type { Tables } from '@/types/supabase';

interface MatchHistoryClientProps {
  player: Tables<'player'>;
  matchHistory?: MatchWithRating[];
}

export default function MatchHistoryClient({ player, matchHistory }: MatchHistoryClientProps) {
  const locale = useLocale();
  const tMatches = useTranslations('Matches');
  const [matches, setMatches] = useState<MatchWithRating[]>(matchHistory || []);
  const [loading, setLoading] = useState(!matchHistory);

  useEffect(() => {
    // Skip fetch if match history was provided
    if (matchHistory) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await ratingModel.getMatchHistoryForPlayer({ slug: player.username || '' });
        if (!mounted) return;
        setMatches(data);
      } catch (error) {
        console.error('Failed to load match history:', error);
        if (!mounted) return;
        setMatches([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [player.username, matchHistory]);

  return (
    <MatchListGrid matches={matches} loading={loading} locale={locale} title={tMatches('title')} />
  );
}
